import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import StationsOnMap, { StationMapPin } from 'components/StationsOnMap/StationsOnMap'
import { Point } from 'geojson'
import FacilityDialogGeoOverlay from 'pages/Assets/shared/FacilityDialogGeoOverlay'
import {
  applyAssetDialogMapCamera,
  AssetDialogCameraCancel,
  AssetDialogMapMode,
} from 'pages/Assets/shared/assetDialogMapCamera'
import { FacilityDialogContextPin } from 'pages/Assets/shared/useFacilityDialogGeoContext'
import clsx from 'clsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IMapboxElement, IRobotInfo } from 'utils/interfaces'

import style from './AssetGeoPointMap.module.css'

type AssetGeoPointMapProps = {
  mode: AssetDialogMapMode
  /** Existing / current geo point — used for Edit camera framing on open. */
  point?: Point | null
  /** Facility base point — create / edit-without-point camera (UX-DIALOG-GEO). */
  facilityPoint?: Point | null
  pins: StationMapPin[]
  /** Read-only facility / other SP pins. */
  contextPins?: FacilityDialogContextPin[]
  /** Facility robots with lastPosition (live or static geo). */
  contextRobots?: IRobotInfo[]
  /** Skip camera until facility context finished loading (avoids Europe→facility flicker). */
  contextLoading?: boolean
  onPointChanged: (point: Point) => void
  className?: string
  /**
   * Change when the dialog reopens or switches create↔edit / facility so the camera
   * is applied again (without re-zooming on every pin click).
   */
  cameraResetKey?: string | number
}

/**
 * Shared map for New/Edit Station and New/Edit Maintenance Point dialogs:
 * create → facility geo (fallback Europe); edit → close zoom on the existing point.
 */
export default function AssetGeoPointMap({
  mode,
  point,
  facilityPoint,
  pins,
  contextPins = [],
  contextRobots = [],
  contextLoading = false,
  onPointChanged,
  className,
  cameraResetKey,
}: AssetGeoPointMapProps) {
  const { t } = useTranslation()
  const mapRef = useRef<IMapboxElement>(null)
  const [mapInited, setMapInited] = useState(false)
  const cameraAppliedForKeyRef = useRef<string | null>(null)
  const cancelCameraRef = useRef<AssetDialogCameraCancel | null>(null)
  const pointRef = useRef(point)
  pointRef.current = point
  const facilityPointRef = useRef(facilityPoint)
  facilityPointRef.current = facilityPoint
  const contextPinsRef = useRef(contextPins)
  contextPinsRef.current = contextPins

  const cameraIdentity = `${cameraResetKey ?? ''}:${mode}`

  const applyCamera = useCallback(() => {
    if (contextLoading) {
      return
    }
    const map = mapRef.current?.getMap()
    if (!map) {
      return
    }
    if (cameraAppliedForKeyRef.current === cameraIdentity) {
      return
    }
    cameraAppliedForKeyRef.current = cameraIdentity
    cancelCameraRef.current?.()
    cancelCameraRef.current = applyAssetDialogMapCamera(
      map,
      mode,
      pointRef.current,
      facilityPointRef.current,
      contextPinsRef.current.map(pin => pin.point)
    )
  }, [cameraIdentity, mode, contextLoading])

  useEffect(() => {
    cameraAppliedForKeyRef.current = null
    cancelCameraRef.current?.()
    cancelCameraRef.current = null
  }, [cameraResetKey])

  useEffect(() => {
    return () => {
      cancelCameraRef.current?.()
      cancelCameraRef.current = null
    }
  }, [])

  const onMapInited = useCallback(() => {
    setMapInited(true)
  }, [])

  useEffect(() => {
    if (!mapInited) {
      return
    }
    applyCamera()
  }, [mapInited, applyCamera])

  const mapInstance = mapInited ? mapRef.current?.getMap() : undefined

  return (
    <div className={style.root}>
      <div className={clsx(style.map, className)}>
        <MapboxCommon disableRuler={true} ref={mapRef} onMapInited={onMapInited}>
          {mapInited && (
            <>
              <FacilityDialogGeoOverlay
                map={mapInstance}
                contextPins={contextPins}
                robots={contextRobots}
              />
              <StationsOnMap map={mapInstance} pins={pins} onPointChanged={onPointChanged} />
            </>
          )}
        </MapboxCommon>
      </div>
      <p className={style.hint}>{t('assets.dialog_save_requirements')}</p>
    </div>
  )
}
